import { FC, useRef } from 'react';

import HotTable, { HotColumn } from '@handsontable/react';
import { IList } from 'core/API/listApi';
import { sortBy, toUpper } from 'lodash';
import { IRetailer } from 'shared/types';
import { CellValue, SourceData } from 'pages/ListManagement/types';
import { arrayToKeyValue } from 'utils/arrays';
import { MSecondaryTargetImageHeader, translateHeaders } from './helpers/HeadersList';
import { numberValidator, retailerValidator } from '../Validators';
import Grid from '../Grid';
import { requireTitle } from './helpers/utils';
import RetailerRenderer from './helpers/CustomDropboxRenderer';

export const MSecondaryTargetImage: FC<{
    list: IList;
    toolId: number;
    retailers: IRetailer[];
}> = ({ list, toolId, retailers }) => {
    const hotRef = useRef<React.ElementRef<typeof HotTable> | null>(null);
    const translatedHeaders = translateHeaders(MSecondaryTargetImageHeader);
    const sortedRetailer = sortBy(retailers, [(o) => toUpper(o.name)]);

    const afterValidate = (listToValidate: IList) => {
        const newData = [...listToValidate.data].map((row) => {
            if (row[2] === null) {
                const newRow = [...row];
                newRow[2] = false;
                return newRow;
            }
            return row;
        });

        return { ...listToValidate, data: newData };
    };

    const beforeDownload = (listToDownload: CellValue[][]) => {
        const keyValueRetailers = arrayToKeyValue(retailers, 'id');

        const newData = listToDownload.map((row) => {
            const newRow = [...row];
            newRow[0] = keyValueRetailers[newRow[0] as string].name;
            return newRow;
        });

        return newData;
    };

    const beforeUpload = (listToUpload: SourceData) => {
        const keyValueRetailers = arrayToKeyValue(retailers, 'name');

        return listToUpload.map((data) => {
            const newRow = [...data];
            newRow[0] = keyValueRetailers[newRow[0] as string].id;

            return newRow;
        });
    };

    return (
        <Grid
            toolId={toolId}
            list={list}
            headers={MSecondaryTargetImageHeader}
            beforeUpload={beforeUpload}
            paramHotRef={hotRef}
            beforeDownload={beforeDownload}
            afterValidate={afterValidate}
        >
            <HotColumn
                validator={retailerValidator(retailers)}
                title={requireTitle(translatedHeaders[0])}
                readOnly={true}
            >
                <RetailerRenderer customList={sortedRetailer} hotInstance={hotRef} hot-renderer />
            </HotColumn>
            <HotColumn
                allowEmpty={false}
                title={requireTitle(translatedHeaders[1])}
                type="numeric"
                validator={numberValidator()}
            />
            <HotColumn
                title={translatedHeaders[2]}
                type="checkbox"
                allowEmpty
                checkedTemplate={true}
                uncheckedTemplate={false}
            />
        </Grid>
    );
};
